<template>
  <div class="maker-page">
    <el-button class="mobile-maker-toggle" size="small" @click="mobileMenuOpen = !mobileMenuOpen">
      <i class="el-icon-menu"></i> 选择社团
      <i :class="mobileMenuOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
    </el-button>

    <div class="maker-layout">
      <el-card class="maker-sidebar" :class="{ 'mobile-open': mobileMenuOpen }" shadow="never">
        <div slot="header" class="sidebar-heading">
          <strong>社团索引</strong>
          <span>{{ makers.length.toLocaleString() }} 个</span>
        </div>
        <el-input
          v-model="searchText"
          clearable
          size="small"
          prefix-icon="el-icon-search"
          placeholder="搜索社团名称"
          aria-label="搜索社团名称"
          @input="visibleLimit = 100"
        ></el-input>
        <div class="list-caption">按收录作品数浏览</div>
        <app-scrollbar
          class="maker-list"
          v-infinite-scroll="loadMore"
          :infinite-scroll-disabled="visibleLimit >= filteredMakers.length"
          :infinite-scroll-distance="80"
        >
          <el-button
            v-for="maker in visibleMakers"
            :key="maker.value"
            class="maker-list-item"
            :class="{ selected: selectedMaker && selectedMaker.value === maker.value }"
            type="text"
            @click="selectMaker(maker)"
          >
            <span class="maker-initial">
              <img v-if="maker.avatar" :src="maker.avatar" alt="" loading="lazy" @error="maker.avatar = ''">
              <template v-else>{{ maker.name.charAt(0).toUpperCase() }}</template>
            </span>
            <span class="maker-list-name">{{ maker.name }}</span>
            <span class="maker-list-count">{{ maker.count }}</span>
          </el-button>
          <div v-if="!listLoading && filteredMakers.length === 0" class="list-empty">没有找到匹配的社团</div>
          <div v-if="listLoading" class="list-empty">正在加载社团…</div>
        </app-scrollbar>
        <div class="sidebar-footer">已收录 <strong>{{ makers.length.toLocaleString() }}</strong> 个社团</div>
      </el-card>

      <el-card class="maker-detail" shadow="never" v-loading="detailLoading">
        <app-scrollbar class="detail-scroll">
        <template v-if="makerInfo">
          <section class="maker-hero">
            <div class="breadcrumb">社团目录 <i class="el-icon-arrow-right"></i> <strong>{{ makerInfo.name }}</strong></div>
            <div class="hero-row">
              <el-avatar class="maker-avatar" shape="square" :size="76" :src="makerInfo.avatar || undefined" icon="el-icon-user-solid"></el-avatar>
              <div class="maker-identity">
                <div class="section-kicker">CIRCLE PROFILE</div>
                <h1>{{ makerInfo.name }}</h1>
                <div class="maker-id">{{ makerInfo.dlsiteId ? 'DLsite ID · ' + makerInfo.dlsiteId : '社团资料' }}</div>
              </div>
              <div class="hero-count"><strong>{{ games.length }}</strong><span>收录作品</span></div>
            </div>
            <div class="external-links">
              <el-button
                v-for="(link, index) in externalLinks"
                :key="link.label"
                :type="index === 0 ? 'primary' : 'default'"
                size="small"
                @click="openLink(link.url)"
              >{{ link.label }} <i class="el-icon-top-right"></i></el-button>
              <span v-if="externalLinks.length" class="external-hint">前往外部站点</span>
            </div>
          </section>

          <section class="works-section">
            <div class="works-heading">
              <div>
                <div class="section-kicker">SELECTED WORKS</div>
                <h2>社团作品 <small>{{ games.length }} 部作品</small></h2>
              </div>
              <div class="sort-control">
                <span>排序</span>
                <el-radio-group v-model="sortMode" size="mini">
                  <el-radio-button label="release">最新发售</el-radio-button>
                  <el-radio-button label="score">评分优先</el-radio-button>
                  <el-radio-button label="sale">销量优先</el-radio-button>
                </el-radio-group>
              </div>
            </div>

            <div v-if="sortedGames.length" class="game-grid">
              <el-card v-for="game in visibleGames" :key="game.id" class="work-card" shadow="hover" :body-style="{ padding: '10px' }">
                <div class="game-cover">
                  <span class="cover-placeholder">WORK</span>
                  <img v-if="game.covers && game.covers[0]" :src="game.covers[0]" alt="作品封面" loading="lazy" @error="hideBrokenImage">
                </div>
                <div class="game-details">
                  <div class="game-code">{{ game.gameId }}</div>
                  <a v-if="game.dlsiteUrl && !game.hasDelete" class="game-title" :href="game.dlsiteUrl" target="_blank" rel="noopener noreferrer">{{ game.name }}</a>
                  <div v-else class="game-title">{{ game.name }}</div>
                  <div class="game-tags">
                    <el-tag v-if="game.type" size="mini" type="success">{{ game.type.split('/')[0] }}</el-tag>
                    <el-tag v-if="game.language" size="mini" type="info">{{ game.language }}</el-tag>
                  </div>
                  <div class="game-bottom">
                    <div class="release-date">发售日期<strong>{{ game.releaseTime || '—' }}</strong></div>
                    <div class="game-numbers">
                      <div class="game-score"><i class="el-icon-star-on"></i> {{ formatScore(game.score) }} · {{ formatNumber(game.saleCount) }} 销量</div>
                      <div class="game-price">¥{{ formatNumber(game.price) }}<small> JPY</small></div>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>
            <div v-else class="works-empty">暂无收录作品</div>
            <div v-if="visibleLimitGames < sortedGames.length" class="more-wrapper">
              <el-button size="small" @click="visibleLimitGames += 8">查看更多作品 <i class="el-icon-arrow-down"></i></el-button>
            </div>
          </section>
        </template>
        <div v-else-if="!detailLoading" class="detail-empty">{{ detailError || '选择左侧社团查看详情' }}</div>
        </app-scrollbar>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppScrollbar from '@/components/AppScrollbar.vue'

export default {
  name: 'MakerNew',
  components: { AppScrollbar },
  data() {
    return {
      makers: [],
      selectedMaker: null,
      makerInfo: null,
      searchText: '',
      visibleLimit: 100,
      visibleLimitGames: 8,
      sortMode: 'release',
      listLoading: true,
      detailLoading: true,
      detailError: '',
      mobileMenuOpen: false,
      requestId: 0
    }
  },
  computed: {
    filteredMakers() {
      const key = this.searchText.trim().toLocaleLowerCase()
      return key ? this.makers.filter(maker => maker.name.toLocaleLowerCase().includes(key)) : this.makers
    },
    visibleMakers() {
      return this.filteredMakers.slice(0, this.visibleLimit)
    },
    games() {
      return this.makerInfo && Array.isArray(this.makerInfo.gameList) ? this.makerInfo.gameList : []
    },
    sortedGames() {
      const games = this.games.slice()
      if (this.sortMode === 'score') games.sort((a, b) => Number(b.score || 0) - Number(a.score || 0))
      else if (this.sortMode === 'sale') games.sort((a, b) => Number(b.saleCount || 0) - Number(a.saleCount || 0))
      else games.sort((a, b) => String(b.releaseTime || '').localeCompare(String(a.releaseTime || '')))
      return games
    },
    visibleGames() {
      return this.sortedGames.slice(0, this.visibleLimitGames)
    },
    externalLinks() {
      if (!this.makerInfo) return []
      const info = this.makerInfo
      return [
        { label: 'DLsite', url: info.dlsiteUrl },
        { label: 'Ci-en', url: info.ciEnUrl },
        { label: 'FANZA', url: info.dmmUrl },
        { label: 'Pixiv', url: info.pixivUrl },
        { label: 'X / Twitter', url: info.twitterUrl },
        { label: '官网', url: info.homepageUrl },
        { label: '博客', url: info.blogUrl }
      ].filter(link => /^https?:\/\//i.test(link.url || ''))
    }
  },
  mounted() {
    axios.get('/api/open/select/maker').then(res => {
      if (res.data.code !== 200 || !Array.isArray(res.data.data)) throw new Error('社团列表加载失败')
      this.makers = res.data.data.map(maker => {
        const match = maker.label.match(/\((\d+)\)$/)
        return {
          value: maker.value,
          name: maker.label.replace(/\(\d+\)$/, ''),
          count: match ? Number(match[1]) : 0,
          avatar: maker.avatar || ''
        }
      })
      this.listLoading = false
      const selected = this.makers.find(maker => String(maker.value) === String(this.$route.query.id)) || this.makers[0]
      if (selected) this.selectMaker(selected)
      else this.detailLoading = false
    }).catch(() => {
      this.listLoading = false
      this.detailLoading = false
      this.detailError = '社团列表加载失败，请稍后重试'
    })
  },
  watch: {
    '$route.query.id'(id) {
      if (!this.makers.length || (this.selectedMaker && String(this.selectedMaker.value) === String(id))) return
      const maker = this.makers.find(item => String(item.value) === String(id))
      if (maker) this.selectMaker(maker)
    }
  },
  methods: {
    loadMore() {
      if (this.visibleLimit < this.filteredMakers.length) this.visibleLimit += 100
    },
    selectMaker(maker) {
      this.selectedMaker = maker
      this.makerInfo = null
      this.detailError = ''
      this.detailLoading = true
      this.visibleLimitGames = 8
      this.mobileMenuOpen = false
      const requestId = ++this.requestId
      if (String(this.$route.query.id) !== String(maker.value)) this.$router.replace({ path: '/maker', query: { id: maker.value } })
      axios.get('/api/open/maker/' + encodeURIComponent(maker.value)).then(res => {
        if (requestId !== this.requestId) return
        if (res.data.code !== 200 || !res.data.data) throw new Error('社团详情加载失败')
        this.makerInfo = res.data.data
      }).catch(() => {
        if (requestId === this.requestId) this.detailError = '社团详情加载失败，请选择其他社团或稍后重试'
      }).finally(() => {
        if (requestId === this.requestId) this.detailLoading = false
      })
    },
    openLink(url) {
      if (/^https?:\/\//i.test(url || '')) window.open(url, '_blank', 'noopener,noreferrer')
    },
    hideBrokenImage(event) {
      event.target.style.display = 'none'
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    formatScore(value) {
      return Number(value || 0).toFixed(2)
    }
  }
}
</script>

<style scoped>
.maker-page { text-align: left; padding: 3px 0 12px; color: #25342f; }
.maker-layout { display: grid; grid-template-columns: 264px minmax(0, 1fr); gap: 16px; align-items: start; }
.maker-sidebar, .maker-detail { border: 1px solid rgba(226, 233, 225, .76); border-radius: 17px; background: rgba(255, 255, 252, .83); box-shadow: 0 16px 40px rgba(24, 47, 36, .10); backdrop-filter: blur(12px); }
.maker-sidebar { position: sticky; top: 72px; }
.maker-sidebar >>> .el-card__header { padding: 21px 18px 15px; border-bottom: 0; }
.maker-sidebar >>> .el-card__body { padding: 0 12px; }
.sidebar-heading { display: flex; justify-content: space-between; align-items: baseline; }
.sidebar-heading strong { font-size: 16px; }
.sidebar-heading span { font-size: 11px; color: #85958b; }
.maker-sidebar >>> .el-input { padding: 0 6px; margin-bottom: 14px; }
.maker-sidebar >>> .el-input__inner { border: 1px solid #dce7de; background: rgba(240, 246, 240, .82); border-radius: 9px; color: #2c5043; }
.list-caption { margin: 0 -12px; padding: 13px 18px 9px; border-top: 1px solid #e7eee8; color: #8da097; font-size: 10px; letter-spacing: .09em; font-weight: 700; }
.maker-list { max-height: calc(100vh - 275px); min-height: 160px; padding-bottom: 10px; }
.maker-list-item.el-button { width: 100%; margin: 1px 0; padding: 8px 9px; display: flex; align-items: center; text-align: left; border-radius: 9px; color: #40544a; font-size: 12px; }
.maker-list-item.el-button:hover { background: rgba(229, 240, 232, .75); }.maker-list-item.selected { background: rgba(212, 233, 220, .85); color: #176558; font-weight: 700; }
.maker-list-item >>> span { min-width: 0; }
.maker-list-item >>> > span { width: 100%; display: flex; align-items: center; gap: 8px; }
.maker-initial { width: 29px; height: 29px; min-width: 29px; display: inline-flex; align-items: center; justify-content: center; overflow: hidden; border-radius: 8px; background: #edf2ed; color: #728b7d; font-family: Georgia, serif; }
.maker-initial img { display: block; width: 100%; height: 100%; object-fit: cover; }
.selected .maker-initial { background: #c9e3d3; color: #276a58; }
.maker-list-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.maker-list-count { color: #8fa299; font-size: 11px; }.list-empty { padding: 20px 8px; text-align: center; color: #8a9a90; font-size: 12px; }
.sidebar-footer { margin: 0 -12px; padding: 13px 18px; border-top: 1px solid #e7eee8; color: #8a9c90; font-size: 11px; }.sidebar-footer strong { color: #2d705e; }
.maker-detail { min-height: 580px; }.maker-detail >>> .el-card__body { padding: 0; }
.maker-hero { padding: 24px 26px 25px; border-bottom: 1px solid rgba(220, 234, 220, .8); background: linear-gradient(120deg, rgba(255, 255, 252, .68), rgba(221, 239, 225, .52)); }
.breadcrumb { font-size: 11px; color: #84978a; }.breadcrumb i { margin: 0 6px; font-size: 10px; }.breadcrumb strong { color: #3e6b57; }
.hero-row { display: flex; align-items: center; gap: 17px; margin-top: 24px; }.maker-avatar { flex: none; border: 3px solid rgba(255,255,255,.8); border-radius: 17px; box-shadow: 0 6px 18px rgba(31, 65, 42, .13); }
.maker-identity { min-width: 0; flex: 1; }.section-kicker { color: #4c8a77; font-size: 10px; font-weight: 800; letter-spacing: .18em; }.maker-identity h1 { font-size: 27px; line-height: 1.25; margin: 5px 0 6px; overflow-wrap: anywhere; }.maker-id { font-size: 11px; color: #81968a; }
.hero-count { border-left: 1px solid #d4e4d7; padding-left: 18px; text-align: right; flex: none; }.hero-count strong { display: block; color: #24705c; font-family: Georgia, serif; font-size: 30px; line-height: 1; }.hero-count span { display: block; color: #91a99b; font-size: 11px; margin-top: 6px; }
.external-links { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin-top: 20px; }.external-links >>> .el-button { margin: 0; border-color: #d4e2d7; color: #376657; background: rgba(255,255,255,.75); font-weight: 600; }.external-links >>> .el-button--primary { background: #2c7464; border-color: #2c7464; color: white; }.external-links >>> .el-button:hover { border-color: #83ae94; }.external-hint { font-size: 10px; color: #a4b1a5; margin-left: 3px; }
.works-section { padding: 25px 26px 30px; }.works-heading { display: flex; justify-content: space-between; align-items: end; gap: 14px; padding-bottom: 17px; border-bottom: 1px solid rgba(221, 232, 222, .85); }.works-heading h2 { font-size: 21px; margin: 5px 0 0; }.works-heading small { font-size: 11px; font-weight: 400; color: #90a297; margin-left: 6px; }.sort-control { display: flex; align-items: center; gap: 8px; }.sort-control > span { color: #9aa99f; font-size: 11px; }.sort-control >>> .el-radio-button__inner { color: #748a7b; border-color: #d8e6da; background: rgba(255,255,255,.6); padding: 8px; }.sort-control >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner { color: #286c59; background: #e2f0e7; border-color: #b8d7c3; box-shadow: -1px 0 0 0 #b8d7c3; }
.game-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin-top: 17px; }.work-card { min-width: 0; border-radius: 12px; border-color: rgba(220, 231, 220, .9); background: rgba(255, 255, 255, .74); }.work-card >>> .el-card__body { display: flex; gap: 11px; min-height: 158px; }.game-cover { width: 102px; min-width: 102px; max-height: 148px; position: relative; overflow: hidden; border-radius: 7px; background: linear-gradient(150deg, #a9c4ae, #4f7c69); }.cover-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,.8); font: bold 15px Georgia, serif; letter-spacing: .12em; }.game-cover img { width: 100%; height: 100%; object-fit: cover; display: block; position: relative; }.game-details { min-width: 0; flex: 1; display: flex; flex-direction: column; padding: 4px 0 1px; }.game-code { color: #79a08b; font-size: 10px; font-weight: 800; letter-spacing: .08em; }.game-title { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; color: #26382e; line-height: 1.38; font-size: 13px; font-weight: 700; min-height: 37px; margin: 5px 0 7px; text-decoration: none; overflow-wrap: anywhere; }.game-title:hover { color: #28775e; }.game-tags { display: flex; gap: 4px; flex-wrap: wrap; }.game-tags >>> .el-tag { border: 0; height: 18px; line-height: 18px; font-size: 10px; }.game-bottom { margin-top: auto; display: flex; align-items: end; justify-content: space-between; gap: 6px; }.release-date { font-size: 10px; color: #94a398; }.release-date strong { display: block; color: #617a68; font-size: 10px; margin-top: 3px; font-weight: 500; }.game-numbers { text-align: right; white-space: nowrap; }.game-score { color: #bc8650; font-size: 10px; font-weight: 700; }.game-price { margin-top: 3px; color: #285745; font: bold 17px Georgia, serif; }.game-price small { font: 10px Arial, sans-serif; }.works-empty, .detail-empty { text-align: center; color: #85998b; padding: 70px 20px; font-size: 13px; }.more-wrapper { text-align: center; margin-top: 22px; }.more-wrapper >>> .el-button { color: #2e755e; border-color: #b9d6c3; background: rgba(255,255,255,.7); }.mobile-maker-toggle { display: none; }
@media (max-width: 1450px) { .game-grid { grid-template-columns: 1fr; } }
@media (max-width: 900px) { .maker-layout { grid-template-columns: 210px minmax(0,1fr); gap: 10px; }.maker-hero, .works-section { padding-left: 17px; padding-right: 17px; }.works-heading { display: block; }.sort-control { margin-top: 12px; }.hero-row { gap: 10px; }.maker-identity h1 { font-size: 20px; } }
@media (max-width: 767px) { .mobile-maker-toggle { display: flex; width: 100%; justify-content: space-between; align-items: center; margin-bottom: 9px; color: #266f5a; background: rgba(255,255,255,.82); border-color: #c9dece; }.maker-layout { display: block; }.maker-sidebar { display: none; position: static; margin-bottom: 10px; }.maker-sidebar.mobile-open { display: block; }.maker-list { max-height: 300px; }.maker-detail { min-height: 460px; }.maker-hero { padding: 20px 15px; }.works-section { padding: 20px 12px; }.hero-row { gap: 10px; }.maker-avatar { width: 58px !important; height: 58px !important; border-radius: 12px; }.hero-count { padding-left: 10px; }.hero-count strong { font-size: 23px; }.hero-count span { font-size: 9px; }.maker-identity h1 { font-size: 19px; }.game-cover { width: 87px; min-width: 87px; }.game-score { font-size: 9px; }.game-price { font-size: 15px; } }

/* The surrounding App content supplies the viewport height and the site's background. */
.maker-page { height: 100%; box-sizing: border-box; padding: 0; color: #303133; }
.maker-layout { height: 100%; align-items: stretch; }
.maker-sidebar, .maker-detail { height: 100%; min-height: 0; border-color: rgba(220, 223, 230, .8); background: rgba(255, 255, 255, .58); box-shadow: 0 10px 30px rgba(0, 0, 0, .08); backdrop-filter: blur(8px); }
.maker-sidebar { position: relative; top: auto; display: flex; flex-direction: column; }
.maker-sidebar >>> .el-card__body { min-height: 0; flex: 1; display: flex; flex-direction: column; }
.maker-list { flex: 1; min-height: 0; max-height: none; }
.sidebar-footer { margin-top: auto; }
.maker-detail >>> .el-card__body { height: 100%; box-sizing: border-box; overflow: hidden; }
.detail-scroll { height: 100%; }

.sidebar-heading span, .list-caption, .maker-list-count, .sidebar-footer, .maker-id,
.hero-count span, .external-hint, .works-heading small, .sort-control > span,
.release-date, .works-empty, .detail-empty, .list-empty { color: #909399; }
.sidebar-footer strong, .breadcrumb strong, .hero-count strong, .game-price { color: #409eff; }
.maker-sidebar >>> .el-input__inner { border-color: #dcdfe6; background: rgba(255, 255, 255, .55); color: #606266; }
.list-caption, .sidebar-footer { border-color: rgba(220, 223, 230, .7); }
.maker-list-item.el-button { color: #606266; }
.maker-list-item.el-button:hover { background: rgba(64, 158, 255, .12); color: #409eff; }
.maker-list-item.selected { background: rgba(64, 158, 255, .18); color: #409eff; }
.maker-initial { background: rgba(144, 147, 153, .14); color: #909399; }
.selected .maker-initial { background: rgba(64, 158, 255, .18); color: #409eff; }
.maker-hero { border-bottom-color: rgba(220, 223, 230, .65); background: linear-gradient(120deg, rgba(255, 255, 255, .35), rgba(236, 245, 255, .25)); }
.breadcrumb { color: #909399; }
.section-kicker, .game-code { color: #409eff; }
.hero-count { border-left-color: rgba(220, 223, 230, .8); }
.external-links >>> .el-button { border-color: #dcdfe6; background: rgba(255, 255, 255, .55); color: #606266; }
.external-links >>> .el-button--primary { background: #409eff; border-color: #409eff; color: #fff; }
.external-links >>> .el-button:hover { border-color: #c6e2ff; color: #409eff; }
.external-links >>> .el-button--primary:hover { background: #66b1ff; border-color: #66b1ff; color: #fff; }
.works-heading { border-bottom-color: rgba(220, 223, 230, .75); }
.sort-control >>> .el-radio-button__inner { border-color: #dcdfe6; background: rgba(255, 255, 255, .55); color: #606266; }
.sort-control >>> .el-radio-button__orig-radio:checked + .el-radio-button__inner { background: #409eff; border-color: #409eff; color: #fff; box-shadow: -1px 0 0 0 #409eff; }
.work-card { border-color: rgba(220, 223, 230, .85); background: rgba(255, 255, 255, .53); }
.game-cover {
  width: 124px;
  min-width: 124px;
  height: 93px;
  max-height: none;
  align-self: center;
  background: #f5f7fa;
}
.game-cover img { object-fit: contain; }
.game-bottom { flex-wrap: wrap; }
.game-title { color: #303133; }.game-title:hover { color: #409eff; }
.release-date strong { color: #606266; }
.game-score { color: #e6a23c; }
.more-wrapper >>> .el-button { color: #409eff; border-color: #b3d8ff; background: rgba(255, 255, 255, .55); }

@media (min-width: 1920px) {
  .game-cover { width: 160px; min-width: 160px; height: 120px; }
}

@media (max-width: 767px) {
  .maker-page { position: relative; }
  .mobile-maker-toggle { height: 36px; margin-bottom: 8px; color: #409eff; background: rgba(255,255,255,.6); border-color: #b3d8ff; }
  .maker-layout { height: calc(100% - 44px); }
  .maker-sidebar { display: none; position: absolute; top: 44px; left: 0; right: 0; z-index: 5; height: min(550px, calc(100% - 44px)); margin: 0; }
  .maker-sidebar.mobile-open { display: flex; }
  .maker-list { max-height: none; }
  .maker-detail { height: 100%; min-height: 0; }
  .game-cover { width: 112px; min-width: 112px; height: 84px; }
}
</style>
